import { useState, useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import LawFirm from "../../components/LawFirm";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import Footer from "../../components/Footer";
import NavigationPanel from "../../components/NavigationPanel";
import CTAs from "../../components/CTAs";
import { userService } from "../../services/userService";
import { dashboardService } from "../../services/dashboardService";
import DownloadModalTables from "../../components/DownloadModalTables";
import FeedbackForm from "../../components/FeedbackForm";
import WelcomeScreen from "../../components/WelcomeScreen";

import DateFilter from "../../components/DateFilter";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { ArrowRight, DocumentText } from "iconsax-reactjs";

const DebtorDashboard = () => {
  const navigate = useNavigate();
  const [isLawFirmOpen, setLawFirmOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const openFeedback = useCallback(() => setFeedbackOpen(true), []);
  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);
  const [isWelcomeScreenOpen, setWelcomeScreenOpen] = useState(false);

  const [today, setToday] = useState("");
  const [entries, setEntries] = useState({});
  const [filteredEntries, setFilteredEntries] = useState([]);
  const authUserStr = localStorage.getItem("authUser");
  const userId = authUserStr ? JSON.parse(authUserStr).id : null;
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] =
    useState(false);
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState("");
  const storedUser = JSON.parse(localStorage.getItem("authUser"));
  const userName = storedUser?.name || "Guest";
  const [selectedDebtorId, setSelectedDebtorId] = useState(null);
  const [selectedFormType, setSelectedFormType] = useState(false);
  const [selectedDebtorAddress, setSelectedDebtorAddress] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [summary, setSummary] = useState({
    pendingDebt: 0,
    totalAmount: 0,
    totalRecovered: 0,
    resolvedCount: 0,
    totalInvoiceRevenue: 0,
  });

  const [timePeriodData, setTimePeriodData] = useState({
    "12months": { pendingDebt: 0, totalAmount: 0, totalRecovered: 0, totalInvoiceRevenue: 0 },
    "30days":   { pendingDebt: 0, totalAmount: 0, totalRecovered: 0, totalInvoiceRevenue: 0 },
    "7days":    { pendingDebt: 0, totalAmount: 0, totalRecovered: 0, totalInvoiceRevenue: 0 },
    "24hours":  { pendingDebt: 0, totalAmount: 0, totalRecovered: 0, totalInvoiceRevenue: 0 },
  });
  const [activeTimePeriod, setActiveTimePeriod] = useState("12months");

  // ── Accounting software connection status ──────────────────────────────
  // Gates nothing here (live data still fetches as before) — this just
  // drives whether the "Connect your accounting software" banner shows.
  const [isXeroConnected, setIsXeroConnected] = useState(true);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
    if (!hasSeenWelcome) setWelcomeScreenOpen(true);
  }, []);

  useEffect(() => {
    const date = new Date();
    setToday(date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }));
  }, []);

  const getDateRange = (period) => {
    const now = new Date();
    const startDate = new Date();
    switch (period) {
      case "24hours": startDate.setHours(now.getHours() - 24); break;
      case "7days":   startDate.setDate(now.getDate() - 7); break;
      case "30days":  startDate.setDate(now.getDate() - 30); break;
      case "12months": startDate.setFullYear(now.getFullYear() - 1); break;
      default:        startDate.setFullYear(now.getFullYear() - 1);
    }
    return { startDate: startDate.toISOString(), endDate: now.toISOString() };
  };

  // Check whether the user has an accounting software connection, purely to
  // drive the banner below.
  useEffect(() => {
    const checkXeroConnection = async () => {
      try {
        setIsCheckingConnection(true);
        const status = await userService.getXeroConnectionStatus(userId);
        setIsXeroConnected(!!status?.connected);
      } catch (err) {
        console.error("Failed to check accounting software connection:", err);
        setIsXeroConnected(false);
      } finally {
        setIsCheckingConnection(false);
      }
    };
    if (userId) checkXeroConnection();
  }, [userId]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const periodSummaries = await dashboardService.getSummaryForAllPeriods(userId);
        setTimePeriodData(periodSummaries);
        const defaultSummary = periodSummaries["12months"];
        setSummary(defaultSummary);
        const dateRange = getDateRange("12months");
        const entryData = await dashboardService.getEntries(userId, dateRange);
        setEntries(entryData);
        const totalCases = defaultSummary.pendingDebt + defaultSummary.resolvedCount;
        setData([
          { name: "Resolved", value: totalCases > 0 ? (defaultSummary.resolvedCount / totalCases) * 100 : 0 },
          { name: "Pending",  value: totalCases > 0 ? (defaultSummary.pendingDebt  / totalCases) * 100 : 0 },
        ]);
      } catch (err) {
        console.error("Dashboard fetch failed:", err);
      }
    };
    fetchDashboardData();
  }, [userId]);

  const handleTimePeriodChange = async (period) => {
    setActiveTimePeriod(period);
    setCurrentPage(1);
    try {
      const selectedSummary = timePeriodData[period];
      setSummary(selectedSummary);
      const dateRange = getDateRange(period);
      const entryData = await dashboardService.getEntries(userId, dateRange);
      setEntries(entryData);
      const totalCases = selectedSummary.pendingDebt + selectedSummary.resolvedCount;
      setData([
        { name: "Resolved", value: totalCases > 0 ? (selectedSummary.resolvedCount / totalCases) * 100 : 0 },
        { name: "Pending",  value: totalCases > 0 ? (selectedSummary.pendingDebt  / totalCases) * 100 : 0 },
      ]);
    } catch (err) {
      console.error("Failed to fetch entries for period:", period, err);
    }
  };

  const normalizeEntry = (entry, stage) => {
    if (stage.startsWith("Post-")) {
      return {
        id: entry._id || entry.id,
        name: entry.debtorId?.businessName || entry.firmName || "—",
        amount: entry.debtorId?.amount || "—",
        stage,
        status: entry.debtorId?.status || entry.status || "Pending",
        risk: entry.riskLevel || "N/A",
        action: "Law Firm Assigned",
        isLawFirmAssigned: true,
        raw: entry,
      };
    }
    return {
      id: entry._id || entry.id,
      name: entry.businessName || entry.name || "—",
      amount: entry.amount || entry.invoiceAmount || "—",
      stage,
      status: entry.status || "Pending",
      risk: entry.riskLevel || "N/A",
      action: "Assign Law Firm",
      isLawFirmAssigned: false,
      raw: entry,
    };
  };

  useEffect(() => {
    if (entries) {
      const { lbaEntries = [], lpdEntries = [], lawFirmEntries = [] } = entries;
      const allEntries = [
        ...lbaEntries.map((e) => normalizeEntry(e, "Pre-LBA")),
        ...lpdEntries.map((e) => normalizeEntry(e, "LPD")),
        ...lawFirmEntries.map((e) => {
          let stage = "Post-LBA";
          if (e.formType === "prelba") stage = "Post-PreLBA";
          if (e.formType === "lpd")    stage = "Post-LPD";
          return normalizeEntry(e, stage);
        }),
      ];
      let filtered = selectedStatus && selectedStatus !== "all"
        ? allEntries.filter((entry) => entry.status === selectedStatus)
        : allEntries;
      if (searchQuery) {
        filtered = filtered.filter((entry) =>
          entry.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.amount.toString().includes(searchQuery) ||
          entry.stage.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.risk.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }
      setFilteredEntries(filtered);
    }
  }, [entries, selectedStatus, searchQuery]);

  const currentEntries = filteredEntries.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);

  const onTabSyncWithXero        = useCallback(() => navigate("/setting-account2"),   [navigate]);
  const onTabUploadLegalDocument = useCallback(() => navigate("/documentprocessing"),  [navigate]);
  const onTabNewPreLBA           = useCallback(() => navigate("/lba"),                [navigate]);

  const openLawFirm = useCallback((debtorId, address, formType) => {
    setSelectedDebtorId(debtorId);
    setSelectedFormType(formType);
    setSelectedDebtorAddress(address);
    setLawFirmOpen(true);
  }, []);

  const closeLawFirm            = useCallback(() => { setLawFirmOpen(false); window.location.reload(); }, []);
  const openLogoutConfirmation  = useCallback(() => setLogoutConfirmationOpen(true),  []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const closeWelcomeScreen      = useCallback(() => { setWelcomeScreenOpen(false); localStorage.setItem("hasSeenWelcome", "true"); }, []);
  const openDownloadModalTables  = useCallback(() => setDownloadModalTablesOpen(true),  []);
  const closeDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(false), []);
  const openDateFilter  = useCallback(() => setDateFilterOpen(true),  []);
  const closeDateFilter = useCallback(() => setDateFilterOpen(false), []);
  const handleDateTimeSelect = (dateRange) => setSelectedDateTimeRange(dateRange);

  const handleStatusChange = useCallback((status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    setIsStatusDropdownOpen(false);
  }, []);

  const toggleStatusDropdown = useCallback(() => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
  }, [isStatusDropdownOpen]);

  const [activeTab, setActiveTab] = useState("all");
  const handleSearch = (e) => { setSearchQuery(e.target.value); setCurrentPage(1); };

  const handleFilter = (type) => {
    const { lbaEntries = [], lpdEntries = [], lawFirmEntries = [] } = entries || {};
    let newFiltered = [];
    if (type === "all") {
      newFiltered = [
        ...lbaEntries.map((e) => normalizeEntry(e, "Pre-LBA")),
        ...lpdEntries.map((e) => normalizeEntry(e, "LPD")),
        ...lawFirmEntries.map((e) => normalizeEntry(e, "Post-LBA")),
      ];
    } else if (type === "lba")     newFiltered = lbaEntries.map((e) => normalizeEntry(e, "Pre-LBA"));
    else if (type === "lpd")       newFiltered = lpdEntries.map((e) => normalizeEntry(e, "LPD"));
    else if (type === "lawfirm")   newFiltered = lawFirmEntries.map((e) => normalizeEntry(e, "Post-LBA"));
    if (selectedStatus && selectedStatus !== "all")
      newFiltered = newFiltered.filter((entry) => entry.status === selectedStatus);
    setFilteredEntries(newFiltered);
  };

  const statusColorMap = {
    Resolved:      { bg: "#EFF6FF", color: "#2563EB" },
    "Medium Risk": { bg: "#FFFBEB", color: "#D97706" },
    Pending:       { bg: "#FFFBEB", color: "#D97706" },
    Identified:    { bg: "#FFFBEB", color: "#D97706" },
    "High Risk":   { bg: "#FEF2F2", color: "#DC2626" },
    "Low Risk":    { bg: "#F0FDF4", color: "#16A34A" },
  };

  const statusIconMap = {
    Resolved: "✓", Pending: "●", Identified: "●",
    "High Risk": "●", "Low Risk": "●", "Medium Risk": "●",
  };

  const COLORS   = ["#2563EB", "#93C5FD"];
  const BG_COLOR = "#EEF2FF";

  const [data, setData] = useState([
    { name: "Resolved", value: 0 },
    { name: "Pending",  value: 0 },
  ]);

  const periodLabel = {
    "12months": "Last 12 months",
    "30days":   "Last 30 days",
    "7days":    "Last 7 days",
    "24hours":  "Last 24 hours",
  }[activeTimePeriod];

  const metricCards = [
    {
      label: "Pending Pre-LBA", value: summary?.pendingDebt || 0, format: "count",
      accentBar: "#F59E0B", iconBg: "#FFFBEB", iconColor: "#D97706",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.995 17h.009" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Total Amount", value: summary?.totalAmount || 0, format: "currency",
      accentBar: "#7C3AED", iconBg: "#F5F3FF", iconColor: "#7C3AED",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9.5 13.75c0 .97.75 1.75 1.67 1.75h1.88c.8 0 1.45-.68 1.45-1.53 0-.91-.4-1.24-.99-1.45l-3.01-1.05c-.59-.21-.99-.53-.99-1.45 0-.84.65-1.53 1.45-1.53h1.88c.92 0 1.67.78 1.67 1.75M12 7.5v9" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 12c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 3v4h4M22 2l-5 5" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Total Recovered", value: summary?.totalRecovered || 0, format: "currency",
      accentBar: "#059669", iconBg: "#ECFDF5", iconColor: "#059669",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 6v2.42C22 10 21 11 19.42 11H16V4.01C16 2.9 16.91 2 18.02 2c1.09.01 2.09.45 2.81 1.17C21.55 3.9 22 4.9 22 6Z" stroke="#059669" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 7v14c0 .83.94 1.3 1.6.8l1.71-1.28c.4-.3.96-.26 1.32.1l1.66 1.67c.39.39 1.03.39 1.42 0l1.68-1.68c.35-.35.91-.39 1.3-.09l1.71 1.28c.66.49 1.6.02 1.6-.8V4c0-1.1.9-2 2-2H6C3 2 2 3.79 2 6v1Z" stroke="#059669" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 9h6M6.75 13h4.5" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Invoice Due Amount", value: summary?.totalInvoiceRevenue || 0, format: "currency",
      accentBar: "#2563EB", iconBg: "#EFF6FF", iconColor: "#2563EB",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  const timePeriods = [
    { key: "12months", label: "12 months" },
    { key: "30days",   label: "30 days"   },
    { key: "7days",    label: "7 days"    },
    { key: "24hours",  label: "24 hours"  },
  ];

  const quickActions = [
    {
      label: "Sync with Accounting Software",
      sub: "Update pending invoices and debtor records.",
      onClick: onTabSyncWithXero,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path stroke="#2563EB" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M22 12c0 5.52-4.48 10-10 10s-8.89-5.56-8.89-5.56m0 0h4.52m-4.52 0v5M2 12C2 6.48 6.44 2 12 2c6.67 0 10 5.56 10 5.56m0 0v-5m0 5h-4.44"/>
        </svg>
      ),
    },
    {
      label: "Upload Legal Document",
      sub: "Scan invoices and contracts with AI.",
      onClick: onTabUploadLegalDocument,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "New Pre-LBA Action",
      sub: "Start a new case against a debtor.",
      onClick: onTabNewPreLBA,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="m20.01 18.51-4.95-4.95M15.06 13.56l-3.54 3.54c-.78.78-2.05.78-2.83 0l-4.24-4.24c-.78-.78-.78-2.05 0-2.83l7.07-7.07c.78-.78 2.05-.78 2.83 0l4.24 4.24c.78.78.78 2.05 0 2.83l-3.53 3.53ZM2 21h6M6.56 7.92l7.07 7.07" stroke="#2563EB" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

        /* ── Force page background — beats global.css and Tailwind ── */
        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        /* Override any Tailwind colour class that might land on the same element */
        .dd-root.bg-white,
        .dd-root.bg-neutrals-neutral-50,
        .dd-root.bg-gray-50 {
          background: rgb(241, 245, 249) !important;
        }

        /* Cards */
        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .dd-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.09); }

        .dd-metric-card { cursor: default; }
        .dd-metric-card:hover { transform: translateY(-3px); }
        .dd-metric-card .accent-bar {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          border-radius: 16px 16px 0 0;
        }

        /* Connect accounting software banner */
        .dd-connect-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          background: #FFFBEB;
          border: 1px solid #FDE68A;
          border-radius: 16px;
          padding: 16px 20px;
        }
        .dd-connect-banner-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .dd-connect-banner-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: #FEF3C7;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .dd-connect-banner-title {
          font-size: 14px; font-weight: 700; color: #0F172A;
        }
        .dd-connect-banner-sub {
          font-size: 13px; color: #92400E; margin-top: 2px;
        }
        .dd-connect-banner-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 9px 18px;
          background: #D97706; border: none; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 700; color: #ffffff;
          cursor: pointer; white-space: nowrap;
          transition: background 0.15s;
        }
        .dd-connect-banner-btn:hover { background: #B45309; }

        /* Period toggle */
        .dd-period-bar {
          display: inline-flex;
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .dd-period-btn {
          padding: 8px 20px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B;
          border: none; background: transparent;
          border-right: 1px solid #E2E8F0;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .dd-period-btn:last-child { border-right: none; }
        .dd-period-btn:hover  { background: #F8FAFC; color: #0F172A; }
        .dd-period-btn.dd-active { background: #2563EB; color: #ffffff; }

        /* Search */
        .dd-search {
          display: flex; align-items: center; gap: 8px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          padding: 9px 14px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          width: 300px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .dd-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .dd-search input {
          border: none; outline: none; background: transparent;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px; color: #0F172A; width: 100%;
        }
        .dd-search input::placeholder { color: #94A3B8; }

        /* Table tabs */
        .dd-tab-row {
          display: inline-flex; background: #F1F5F9;
          border-radius: 8px; padding: 3px; gap: 2px;
        }
        .dd-tab {
          padding: 6px 16px; border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B;
          cursor: pointer; border: none; background: transparent;
          transition: background 0.15s, color 0.15s;
        }
        .dd-tab:hover { background: #ffffff; color: #0F172A; }
        .dd-tab.dd-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

        /* Status dropdown */
        .dd-filter-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s;
        }
        .dd-filter-btn:hover { border-color: #2563EB; }

        .dd-dropdown {
          position: absolute; top: calc(100% + 6px); right: 0;
          width: 180px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 10px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12); z-index: 50; overflow: hidden;
        }
        .dd-dropdown-item {
          padding: 10px 16px; font-size: 13px; font-weight: 500;
          color: #0F172A; cursor: pointer; transition: background 0.12s;
        }
        .dd-dropdown-item:hover { background: #F8FAFC; }
        .dd-dropdown-item.dd-selected { color: #2563EB; font-weight: 700; background: #EFF6FF; }

        /* Table */
        .dd-table { width: 100%; border-collapse: collapse; }
        .dd-table thead tr { background: #F8FAFC; }
        .dd-table th {
          padding: 11px 16px; text-align: left;
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          border-bottom: 1px solid #E2E8F0;
        }
        .dd-table td {
          padding: 14px 16px; font-size: 14px; color: #1E293B;
          border-bottom: 1px solid #F1F5F9; vertical-align: middle;
        }
        .dd-table tbody tr { transition: background 0.12s; }
        .dd-table tbody tr:hover { background: #F8FAFC; }
        .dd-table tbody tr:last-child td { border-bottom: none; }

        /* Badge */
        .dd-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 20px;
          font-size: 12px; font-weight: 600;
        }

        /* Pagination */
        .dd-page-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .dd-page-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }

        /* Quick action items */
        .dd-action-item {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 18px; border-radius: 12px;
          border: 1px solid #E2E8F0; background: #ffffff;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
        }
        .dd-action-item:hover {
          border-color: #2563EB;
          box-shadow: 0 4px 16px rgba(37,99,235,0.1);
          transform: translateX(4px);
        }
        .dd-action-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: #EFF6FF;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }

        /* Download btn */
        .dd-dl-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .dd-dl-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }

        /* Count pill */
        .dd-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        /* Pie legend */
        .dd-pie-value {
          font-family: 'DM Mono', monospace;
          font-size: 28px; font-weight: 500; color: #0F172A;
        }

        /* Link / disabled */
        .dd-link { color: #2563EB; font-weight: 600; font-size: 13px; cursor: pointer; text-decoration: none; }
        .dd-link:hover { text-decoration: underline; }
        .dd-link-off { color: #CBD5E1; font-size: 13px; cursor: not-allowed; }

        /* Stage chip */
        .dd-stage-chip {
          background: #F1F5F9; color: #475569;
          padding: 2px 10px; border-radius: 6px;
          font-size: 12px; font-weight: 600;
        }

        /* Name / amount cells */
        .dd-name-cell   { font-weight: 600; color: #0F172A; }
        .dd-amount-cell { font-family: 'DM Mono', monospace; font-size: 13px; }
      `}</style>

      {/* ── Root: inline style ensures background even if class is overridden ── */}
      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        {/* ── Main ── */}
        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
                Welcome back, {userName || "User"} 👋
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>{today}</p>
            </div>
            <div className="dd-search">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input type="text" placeholder="Search debtors, amounts, stages…" value={searchQuery} onChange={handleSearch}/>
            </div>
          </div>

          {/* Connect accounting software banner — shown until a connection is confirmed */}
          {!isCheckingConnection && !isXeroConnected && (
            <div className="dd-connect-banner">
              <div className="dd-connect-banner-left">
                <div className="dd-connect-banner-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9.5 13.75c0 .97.75 1.75 1.67 1.75h1.88c.8 0 1.45-.68 1.45-1.53 0-.91-.4-1.24-.99-1.45l-3.01-1.05c-.59-.21-.99-.53-.99-1.45 0-.84.65-1.53 1.45-1.53h1.88c.92 0 1.67.78 1.67 1.75M12 7.5v9" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 12c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <div className="dd-connect-banner-title">Connect your accounting software</div>
                  <div className="dd-connect-banner-sub">
                    Link Accounting software to automatically pull in overdue invoices.
                  </div>
                </div>
              </div>
              <button className="dd-connect-banner-btn" onClick={onTabSyncWithXero}>
                Connect now
                <ArrowRight size="16" color="#ffffff" />
              </button>
            </div>
          )}

          {/* Period toggle */}
          <div className="dd-period-bar">
            {timePeriods.map((tp) => (
              <button
                key={tp.key}
                className={`dd-period-btn${activeTimePeriod === tp.key ? " dd-active" : ""}`}
                onClick={() => handleTimePeriodChange(tp.key)}
              >
                {tp.label}
              </button>
            ))}
          </div>

          {/* Metric cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
            {metricCards.map((card) => (
              <div key={card.label} className="dd-card dd-metric-card" style={{ padding: 24 }}>
                <div className="accent-bar" style={{ background: card.accentBar }}/>
                <div style={{ marginTop: 8 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, border: "1px solid #E2E8F0" }}>
                    {card.icon}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    {card.label}
                  </div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 28, fontWeight: 500, color: "#0F172A", lineHeight: 1.1 }}>
                    {card.format === "currency"
                      ? `£${(card.value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : card.value}
                  </div>
                  <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 4, background: card.iconBg, color: card.iconColor, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                    ● {periodLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Debt Collection table ── */}
          <div className="dd-card" style={{ overflow: "hidden" }}>
            {/* Header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Debt Collection</span>
                <span className="dd-count-pill">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }}/>
                  {currentEntries?.length ?? 0} entries
                </span>
              </div>
              <button className="dd-dl-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download
              </button>
            </div>

            {/* Filter row */}
            <div style={{ padding: "12px 24px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFD" }}>
              <div className="dd-tab-row">
                {[
                  { key: "all",      label: "View all"      },
                  { key: "lba",      label: "Pre-LBA"       },
                  { key: "lawfirm",  label: "Post pre-LBA"  },
                  { key: "lpd",      label: "LPD"           },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`dd-tab${activeTab === tab.key ? " dd-active" : ""}`}
                    onClick={() => { setActiveTab(tab.key); handleFilter(tab.key); }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={{ position: "relative" }}>
                <button className="dd-filter-btn" onClick={toggleStatusDropdown}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Status: {selectedStatus === "all" ? "All" : selectedStatus}
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
                    style={{ transform: isStatusDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                    <path d="m19 9-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {isStatusDropdownOpen && (
                  <div className="dd-dropdown">
                    {[{ key: "all", label: "All Statuses" }, { key: "Pending", label: "Pending" }, { key: "Resolved", label: "Resolved" }].map((s) => (
                      <div
                        key={s.key}
                        className={`dd-dropdown-item${selectedStatus === s.key ? " dd-selected" : ""}`}
                        onClick={() => handleStatusChange(s.key)}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table className="dd-table">
                <thead>
                  <tr>
                    {["Name", "Amount", "Stage", "Risk", "Status", "Action"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentEntries && currentEntries.length > 0 ? (
                    currentEntries.map((lists) => {
                      const riskStyle = statusColorMap[lists.risk]   || { bg: "#F1F5F9", color: "#64748B" };
                      const statStyle = statusColorMap[lists.status] || { bg: "#F1F5F9", color: "#64748B" };
                      return (
                        <tr key={lists.id}>
                          <td className="dd-name-cell">{lists.name}</td>
                          <td className="dd-amount-cell">
                            £{(lists.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td><span className="dd-stage-chip">{lists.stage}</span></td>
                          <td>
                            <span className="dd-badge" style={{ background: riskStyle.bg, color: riskStyle.color }}>
                              {statusIconMap[lists.risk]} {lists.risk}
                            </span>
                          </td>
                          <td>
                            <span className="dd-badge" style={{ background: statStyle.bg, color: statStyle.color }}>
                              {statusIconMap[lists.status]} {lists.status}
                            </span>
                          </td>
                          <td>
                            {lists.isLawFirmAssigned || lists.status === "Resolved" ? (
                              <span className="dd-link-off">{lists.action}</span>
                            ) : (
                              <a className="dd-link" onClick={() => {
                                let formType = "";
                                if (lists.stage === "Pre-LBA")  formType = "lba";
                                else if (lists.stage === "LPD") formType = "lpd";
                                else if (lists.stage === "Post-LBA") formType = "lawfirm";
                                openLawFirm(lists.id, lists.address || "", formType);
                              }}>
                                {lists.action}
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>
                        No entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderTop: "1px solid #E2E8F0", background: "#FAFBFD" }}>
              <button className="dd-page-btn" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
                ← Previous
              </button>
              <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button className="dd-page-btn" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>
                Next →
              </button>
            </div>
          </div>

          {/* ── Bottom row ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

            {/* Pie chart */}
            <div className="dd-card" style={{ padding: 24 }}>
              <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 16, marginBottom: 24 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Outcome &amp; Costs</div>
                <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Case resolution breakdown</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
                <div style={{ width: 200, height: 200, flexShrink: 0 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={[{ value: data[0]?.value || 0 }, { value: 100 - (data[0]?.value || 0) }]}
                        cx="50%" cy="50%" innerRadius={64} outerRadius={84}
                        dataKey="value" stroke="none" paddingAngle={4} cornerRadius={50} startAngle={90}>
                        <Cell fill={COLORS[0]}/><Cell fill={BG_COLOR}/>
                      </Pie>
                      <Pie data={[{ value: data[1]?.value || 0 }, { value: 100 - (data[1]?.value || 0) }]}
                        cx="50%" cy="50%" innerRadius={40} outerRadius={58}
                        dataKey="value" stroke="none" paddingAngle={4} cornerRadius={50}>
                        <Cell fill={COLORS[1]}/><Cell fill={BG_COLOR}/>
                      </Pie>
                      <Tooltip formatter={(v) => `${v.toFixed(1)}%`}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  {[
                    { label: "Resolved", val: data[0]?.value || 0, color: COLORS[0] },
                    { label: "Pending",  val: data[1]?.value || 0, color: COLORS[1] },
                  ].map((leg) => (
                    <div key={leg.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: leg.color, flexShrink: 0 }}/>
                      <div>
                        <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>{leg.label}</div>
                        <div className="dd-pie-value">{leg.val.toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="dd-card" style={{ padding: 24 }}>
              <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Quick Actions</div>
                <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Jump straight into common tasks</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {quickActions.map((action) => (
                  <div key={action.label} className="dd-action-item" onClick={action.onClick}>
                    <div className="dd-action-icon">{action.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{action.label}</div>
                      <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>{action.sub}</div>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M8.91 19.92 15.43 13.4c.78-.78.78-2.05 0-2.83L8.91 4.05" stroke="#CBD5E1" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* ── Modals ── */}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDownloadModalTables}>
          <DownloadModalTables onClose={closeDownloadModalTables} tableData={filteredEntries} tag="dashboard"/>
        </PortalPopup>
      )}
      {isLawFirmOpen && (
        <PortalPopup overlayColor="rgba(0,0,0,0.25)" placement="Centered" onOutsideClick={closeLawFirm}>
          <LawFirm onClose={closeLawFirm} debtorId={selectedDebtorId} formType={selectedFormType} debtorAddress={selectedDebtorAddress}/>
        </PortalPopup>
      )}
      {isStatusDropdownOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 5 }} onClick={() => setIsStatusDropdownOpen(false)}/>
      )}
      {isDateFilterOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeDateFilter}>
          <DateFilter onClose={closeDateFilter} onDateTimeSelect={handleDateTimeSelect}/>
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(0,0,0,0.25)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation}/>
        </PortalPopup>
      )}
      {isWelcomeScreenOpen && (
        <PortalPopup overlayColor="rgba(0,0,0,0.25)" placement="Centered">
          <WelcomeScreen onClose={closeWelcomeScreen}/>
        </PortalPopup>
      )}
    </>
  );
};

export default DebtorDashboard;